/**
 * Payment Utilities
 * Shared helper functions for managing window states, data formatting, and environment checks.
 *
 * @module paymentUtils
 */

import DOMPurify from 'isomorphic-dompurify'
import {
  PAYMENT_AUTH_STATUS,
  POPUPS_CLOSE_DELAY_MS,
  REDIRECT_DELAY_MS,
} from './paymentConstants'

/**
 * Checks if the current window can safely access its opener.
 * Prevents cross-origin errors and handles closed parent windows.
 *
 * @returns {boolean}
 */
export const canAccessOpener = () => {
  if (typeof window === 'undefined' || !window.opener || window.opener.closed) {
    return false
  }

  try {
    return !!window.opener.location.href
  } catch (e) {
    console.warn('[Payment] Cross-origin access to window.opener is prohibited')
    return false
  }
}

/**
 * Checks if payment authentication status is approved.
 *
 * @param {string} status
 * @returns {boolean}
 */
export const isPaymentApproved = (status) => {
  return [PAYMENT_AUTH_STATUS.APPROVED, PAYMENT_AUTH_STATUS.YES].includes(
    status
  )
}

/**
 * Checks if payment authentication status is declined.
 *
 * @param {string} status
 * @returns {boolean}
 */
export const isPaymentDeclined = (status) => {
  return status && !isPaymentApproved(status)
}

/**
 * Safely redirects the parent window from a popup with origin validation.
 *
 * @param {string} targetUrl - URL to redirect to
 * @returns {boolean} - Success status of the redirect attempt
 */
export const safeRedirectToParent = (targetUrl) => {
  if (!canAccessOpener()) {
    console.warn('[Payment] Cannot redirect parent: window.opener inaccessible')
    return false
  }

  try {
    const currentOrigin = window.location.origin
    const validatedUrl = new URL(targetUrl, currentOrigin)

    if (validatedUrl.origin !== currentOrigin) {
      console.error('[Payment] Security: refusing redirect to different origin')
      return false
    }

    console.log('[Payment] Redirecting parent to:', validatedUrl.href)
    window.opener.location.href = validatedUrl.href

    setTimeout(() => window.close(), REDIRECT_DELAY_MS)
    return true
  } catch (error) {
    console.error('[Payment] Error redirecting parent window:', error)
    setTimeout(() => window.close(), POPUPS_CLOSE_DELAY_MS)
    return false
  }
}

/**
 * Safely close the current window with validation.
 *
 * @returns {boolean}
 */
export const safeCloseWindow = () => {
  try {
    if (typeof window !== 'undefined') {
      window.close()
      return true
    }
  } catch (error) {
    console.error('[Payment] Error closing window:', error)
  }
  return false
}

/**
 * Wrap a promise with a custom timeout.
 *
 * @param {Promise} promise
 * @param {number} timeoutMs
 * @param {string} message
 * @returns {Promise}
 */
export const withTimeout = (
  promise,
  timeoutMs,
  message = 'Operation timed out'
) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(message)), timeoutMs)
    ),
  ])
}

/**
 * Validates the basic payment state structure.
 *
 * @param {Object} state
 * @returns {boolean}
 */
export const validatePaymentState = (state) => {
  return (
    state &&
    typeof state === 'object' &&
    typeof state.state === 'string' &&
    state.state !== '' &&
    state.info &&
    typeof state.info === 'object'
  )
}

/**
 * Detects if a popup window was blocked by the browser.
 *
 * @param {Window|null} windowRef
 * @returns {boolean}
 */
export const isPopupBlocked = (windowRef) => {
  if (!windowRef || windowRef.closed) return true

  try {
    windowRef.focus()
    return false
  } catch (e) {
    return true
  }
}

/**
 * Sanitizes data for storage by removing non-serializable properties.
 *
 * @param {Object} data
 * @returns {Object}
 */
export const sanitizeForStorage = (data) => {
  try {
    return JSON.parse(JSON.stringify(data))
  } catch (error) {
    console.error('[Payment] Error sanitizing data for storage:', error)
    return {}
  }
}

/**
 * Checks if a value is numeric.
 *
 * @param {*} value
 * @returns {boolean}
 */
export const isNumeric = (value) => {
  return !isNaN(parseFloat(value)) && isFinite(value)
}

/**
 * Create a nested object structure from a path array.
 *
 * @param {Object} obj - Target object
 * @param {Array<string>} path - Keys representing the path
 * @param {*} value - Value to set at the end of path
 */
export const createNestedObject = (obj, path, value) => {
  if (path.length === 1) {
    obj[path[0]] = value
    return
  }
  if (!obj[path[0]]) {
    obj[path[0]] = {}
  }
  createNestedObject(obj[path[0]], path.slice(1), value)
}

/**
 * Detects payment type (single vs recurrent) from query parameters.
 *
 * @param {Object} queryParams
 * @returns {string} 'single' | 'recurrent'
 */
export const getPaymentTypeFromParams = (queryParams) => {
  return queryParams.ckey ? 'single' : 'recurrent'
}

/**
 * Extracts the donor first name from query params, regardless of casing.
 * `firstName` is set by our own redirects; `firstname` (lowercase) is set
 * by the legacy recurrent payment provider.
 *
 * @param {Object} queryParams
 * @returns {string}
 */
export const getFirstNameFromParams = (queryParams) => {
  return queryParams?.firstName || queryParams?.firstname || ''
}

/**
 * Converts a day/month value from the UI format (GG/MM) to the API format
 * (GG-MM), e.g. "01/01" -> "01-01".
 *
 * @param {string} value
 * @returns {string}
 */
export const formatDayMonthForApi = (value) => {
  return typeof value === 'string' ? value.replace('/', '-') : value
}

/**
 * Ensures a URL uses the HTTPS protocol, except for localhost.
 *
 * @param {string} url
 * @returns {string}
 */
export const ensureHttpsUrl = (url) => {
  if (!url) return url
  if (url.includes('localhost') || url.includes('127.0.0.1')) {
    return url.startsWith('http') ? url : `http://${url}`
  }
  if (url.startsWith('http://')) return url.replace('http://', 'https://')
  return url.startsWith('https://') ? url : `https://${url}`
}

/**
 * Parses search parameters into a plain object.
 *
 * @param {URLSearchParams} searchParams
 * @returns {Object}
 */
export const parseQueryParams = (searchParams) => {
  const params = {}
  searchParams.forEach((value, key) => {
    params[key] = value
  })
  return params
}

/**
 * Sanitize user input for safe URL construction.
 * SECURITY: Prevents XSS by stripping all HTML tags.
 *
 * @param {string} value
 * @returns {string}
 */
export const sanitizeForUrl = (value) => {
  if (!value) return ''
  return DOMPurify.sanitize(String(value), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  }).trim()
}

const paymentUtils = {
  canAccessOpener,
  isPaymentApproved,
  isPaymentDeclined,
  safeRedirectToParent,
  safeCloseWindow,
  withTimeout,
  validatePaymentState,
  isPopupBlocked,
  sanitizeForStorage,
  isNumeric,
  createNestedObject,
  getPaymentTypeFromParams,
  getFirstNameFromParams,
  formatDayMonthForApi,
  ensureHttpsUrl,
  parseQueryParams,
  sanitizeForUrl,
}

export default paymentUtils
